import {
  Component,
  Input,
  OnInit,
  PLATFORM_ID,
  Inject,
  Optional,
  OnDestroy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import {
  LucideAngularModule,
  Car,
  Plus,
  Trash2,
  Edit,
  Calendar,
  MapPin,
  User,
  Shield,
  ArrowLeft,
  Star,
  Check,
  X,
} from 'lucide-angular';
import { CommonModule, Location } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
  DialogService,
} from 'primeng/dynamicdialog';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AddVehicleForm } from '../add-vehicle-form/add-vehicle-form';
import { AuthStateService } from '../../../services/auth-state.service';
import { Auto } from '../../../types/vehicle.types';
import { VehicleService } from '../../../services/vehicle.service';
import { NavigationService } from '../../../services/navigation.service';

@Component({
  selector: 'allas-mi-garage',
  imports: [CommonModule, FormsModule, LucideAngularModule, RouterModule],
  templateUrl: 'mi-garage.html',
  providers: [DialogService],
})
export class MiGarage implements OnInit, OnDestroy {
  @Input() showHeader: boolean = true;

  vehiculos: Auto[] = [];
  isLoading: boolean = true;
  isUserLoggedIn: boolean = false;
  editingVehicleId: number | null = null;
  editingNickname: string = '';
  isUpdatingAlias: boolean = false;

  private title = inject(Title);
  private meta = inject(Meta);

  readonly carIcon = Car;
  readonly plusIcon = Plus;
  readonly editIcon = Edit;
  readonly trashIcon = Trash2;
  readonly calendarIcon = Calendar;
  readonly mapPinIcon = MapPin;
  readonly userIcon = User;
  readonly shieldIcon = Shield;
  readonly arrowLeftIcon = ArrowLeft;
  readonly starIcon = Star;
  readonly checkIcon = Check;
  readonly xIcon = X;

  private vehiclesSubscription?: Subscription;
  private authStateSubscription?: Subscription;

  beneficiosGarage = [
    {
      icon: this.carIcon,
      title: 'Repuestos Específicos',
      description:
        'Encuentra repuestos exactos para tu vehículo según marca, modelo y año',
    },
    {
      icon: this.shieldIcon,
      title: 'Garantía Extendida',
      description:
        'Obtén garantías especiales en repuestos para vehículos registrados',
    },
    {
      icon: this.calendarIcon,
      title: 'Recordatorios',
      description:
        'Recibe alertas de mantenimiento y cambio de repuestos importantes',
    },
  ];

  constructor(
    private router: Router,
    private vehiclesService: VehicleService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private authStateService: AuthStateService,
    private navigationService: NavigationService,
    @Optional() private dialogRef: DynamicDialogRef,
    @Optional() private dialogConfig: DynamicDialogConfig,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initializeDialogConfig();
  }

  ngOnInit(): void {
    this.setMetaTags();
    if (isPlatformBrowser(this.platformId)) {
      this.initializeComponent();
    }
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
    // No llamar closeDialog() aquí porque causa navegación no deseada
    // this.closeDialog();
  }

  private setMetaTags() {
    const pageTitle = 'Mi Garaje | Allas Repuestos';
    const description =
      'Gestiona tus vehículos en Allas Repuestos. Encuentra repuestos específicos, garantías extendidas y recordatorios de mantenimiento con envío a domicilio en Honduras.';

    this.title.setTitle(pageTitle);

    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
  }

  agregarVehiculo() {
    if (this.isMobileDevice()) {
      this.navigateToAddVehicle();
    } else {
      this.openAddVehicleModal();
    }
  }

  async eliminarVehiculo(vehiculo: Auto) {
    if (this.shouldDeleteVehicle(vehiculo)) {
      await this.performVehicleDeletion(vehiculo.id!);
    }
  }

  seleccionarVehiculo(vehiculo: Auto) {
    if (vehiculo.id) {
      this.setDefaultVehicleAndNavigate(vehiculo.id);
    }
  }

  closeOrBack() {
    if (this.isMobileDevice()) {
      this.goBack();
    } else {
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    }
  }

  goBack() {
    this.navigationService.goBack();
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

  async refreshVehicles() {
    await this.loadVehiclesData();
  }

  async continuarSinVehiculo() {
    await this.clearDefaultVehicle();
    this.closeOrBack();
  }

  startEditingNickname(vehicle: Auto): void {
    if (!vehicle.id) return;

    this.setEditingState(vehicle);
    this.focusInputField();
  }

  cancelEditingNickname(): void {
    this.resetEditingState();
  }

  async confirmEditingNickname(): Promise<void> {
    if (!this.isValidNickname()) {
      this.cancelEditingNickname();
      return;
    }

    await this.updateVehicleAlias();
  }

  onNicknameKeyDown(event: KeyboardEvent): void {
    this.handleNicknameKeyPress(event);
  }

  isEditingVehicle(vehicle: Auto): boolean {
    return vehicle.id === this.editingVehicleId;
  }

  private initializeDialogConfig(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.dialogConfig?.data?.showHeader !== undefined) {
        this.showHeader = this.dialogConfig.data.showHeader;
      }
    }
  }

  private initializeComponent(): void {
    this.checkAuthenticationStatus();
    this.subscribeToAuthState();
    this.subscribeToVehicles();
    this.scheduleInitialVehicleLoad();
  }

  private subscribeToAuthState(): void {
    this.authStateSubscription = this.authStateService.authState$.subscribe(
      () => this.checkAuthenticationStatus()
    );
  }

  private subscribeToVehicles(): void {
    this.vehiclesSubscription = this.vehiclesService.vehicles$.subscribe(
      (vehicles) => {
        this.vehiculos = vehicles;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private scheduleInitialVehicleLoad(): void {
    setTimeout(() => {
      this.loadInitialVehicles();
    }, 100);
  }

  private async loadInitialVehicles(): Promise<void> {
    await this.loadVehiclesData();
  }

  private async loadVehiclesData(): Promise<void> {
    this.isLoading = true;
    const vehicles = await this.vehiclesService.get();
    this.vehiculos = vehicles;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private checkAuthenticationStatus(): void {
    const previousState = this.isUserLoggedIn;
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    this.isUserLoggedIn = !!(token && userData);
    this.cdr.detectChanges();
  }

  private cleanupSubscriptions(): void {
    if (this.vehiclesSubscription) {
      this.vehiclesSubscription.unsubscribe();
    }
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
  }

  private isMobileDevice(): boolean {
    return window.innerWidth <= 1024;
  }

  private navigateToAddVehicle(): void {
    this.router.navigate(['/garage/agregar-vehiculo']);
  }

  private openAddVehicleModal(): void {
    const dialog = this.dialogService.open(AddVehicleForm, {
      header: 'AGREGAR UN VEHÍCULO',
      width: '90vw',
      appendTo: 'body',
      style: {
        maxWidth: '600px',
      },
      modal: true,
      closable: true,
      data: {
        platformId: this.platformId,
        isModal: true,
      },
    });

    dialog?.onClose.subscribe((closed: boolean) => {
      if (closed) {
        this.handleVehicleAdded();
      }
    });
  }

  private handleVehicleAdded(): void {
    this.refreshVehicles();
    this.closeOrBack();
  }

  private shouldDeleteVehicle(vehiculo: Auto): boolean {
    return !!vehiculo.id;
  }

  private async performVehicleDeletion(vehicleId: number): Promise<void> {
    try {
      await this.vehiclesService.remove(vehicleId);
    } catch (error) {
      alert('Error al eliminar el vehículo. Por favor, inténtalo de nuevo.');
    }
  }

  private async setDefaultVehicleAndNavigate(vehicleId: number) {
    await this.vehiclesService.setDefaultVehicle(vehicleId);
    await this.refreshVehicles();
    this.closeOrBack();
  }

  private async clearDefaultVehicle() {
    const defaultVehicle = this.vehiclesService.getDefaultVehicle();
    this.vehiclesService.setDefaultVehicle(defaultVehicle?.id || 0, true);
  }

  private setEditingState(vehicle: Auto): void {
    this.editingVehicleId = vehicle.id;
    this.editingNickname = vehicle.alias;
    this.isUpdatingAlias = false;
  }

  private focusInputField(): void {
    setTimeout(() => {
      const input = document.querySelector(
        'input[type="text"]'
      ) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  private resetEditingState(): void {
    this.editingVehicleId = null;
    this.editingNickname = '';
    this.isUpdatingAlias = false;
  }

  private isValidNickname(): boolean {
    return !!(this.editingVehicleId && this.editingNickname.trim());
  }

  private async updateVehicleAlias(): Promise<void> {
    this.isUpdatingAlias = true;

    try {
      if (this.isUserLoggedIn) {
        await this.vehiclesService.updateAlias(
          this.editingVehicleId!,
          this.editingNickname.trim()
        );
      }
      this.cancelEditingNickname();
    } catch (error) {
      alert('Error al actualizar el alias. Por favor, inténtalo de nuevo.');
    } finally {
      this.isUpdatingAlias = false;
    }
  }

  private handleNicknameKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmEditingNickname();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditingNickname();
    }
  }

  getVehiculoSeleccionado(): Auto | undefined {
    return this.vehiculos.find((vehiculo) => vehiculo.predeterminado);
  }

  getAnotherVehicles(): Auto[] {
    return this.vehiculos.filter((vehiculo) => !vehiculo.predeterminado);
  }
}
